import React from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import Button from 'jbc-front/components/Button';
import { Upload } from 'jbc-front/components/icons';
import styles from './ImportHealthCheckupInstitution.scss';
import { useNotify } from '../../../../hooks/useNotify';
import { useAsyncTask } from '../../../../components/AsyncTask';
import { FileField } from '../../../../components/react-hook-form/FileField';
import { useMutation } from '../../../../components';
import { IMPORT_HEALTH_CHECKUP_INSTITUTION_CSV } from './mutation';
import { schema, Schema } from './schema';
import { useHistory } from 'react-router-dom';

export const ImportHealthCheckupInstitution = () => {
  const importHealthCheckupInstitutionCsv = useMutation(IMPORT_HEALTH_CHECKUP_INSTITUTION_CSV);
  const notify = useNotify();
  const history = useHistory();
  const { taskRunningProps } = useAsyncTask();

  const methods = useForm<Schema>({
    resolver: yupResolver(schema)
  });

  const { handleSubmit, watch, setValue } = methods;

  const file = watch('file');

  const onSubmit: SubmitHandler<Schema> = async data => {
    if (!data.file) {
      notify('ファイルを選択してください', 'error');
      return;
    }

    await importHealthCheckupInstitutionCsv.mutation({ variables: { input: { file: data.file } } });
    history.push('/settings/health_checkup_institution');
  };

  return (
    <div>
      <div className="l-contents-wrap">
        <h1 className="m-title-main">健康診断実施機関登録・更新</h1>
        <div className={styles.wrap}>
          <div className={styles.head}>登録・更新する健康診断実施機関を記載したファイルを選択してください</div>
          <div className={styles.body}>
            <dl className={styles.label}>
              <dd className={styles.detail}>
                <p>従業員データを対応しているファイル形式はCSVとなります</p>
                <p>
                  更新する場合は健康診断実施機関データを
                  <Link to="/settings/health_checkup_institution" className="u-txt-link" target="_blank">
                    こちら
                  </Link>
                  からダウンロードし更新したい箇所をご修正ください
                </p>
              </dd>
            </dl>
            <FormProvider {...methods}>
              <dl className={styles.label}>
                <dd className={styles.detail}>
                  <FileField.FileContainer>
                    <FileField name="file" />
                    <FileField.File file={file} onRemove={() => setValue('file', undefined)} />
                  </FileField.FileContainer>
                </dd>
              </dl>
              <Button
                icon={<Upload size={16} />}
                primary
                className={styles.large}
                {...taskRunningProps}
                onClick={handleSubmit(onSubmit)}>
                健康診断実施機関登録・編集
              </Button>
            </FormProvider>
          </div>
        </div>
      </div>

      <div className={`u-mt50 ${styles.warningWrap}`}>
        <p className="u-ta-l">
          <span className={styles.warningTitle}>【注意】</span>
          ①IDが一致しない健康診断実施機関の情報をアップロードした場合、
          <span className={styles.warning}>新たな健康診断実施機関としてマスタ登録</span>
          します
          <br />
          ②IDが一致した健康診断実施機関の情報をアップロードした場合、
          <span className={styles.warning}>同一の健康診断実施機関の情報を更新</span>
          します
          <br />
        </p>
      </div>

      <div className={styles.tableWrap}>
        <div className="l-overflow-scroll">
          <table className="m-table-description">
            <thead>
              <tr>
                <th className="m-table-description-item-name">データ項目名</th>
                <th>説明文</th>
                <th className="m-table-description-example">例</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>ID{<span className={styles.required}>必須</span>}</th>
                <td>
                  半角英数5文字まで
                  <br />
                  ※-（半角ハイフン）、_（半角アンダーバー）を含む
                </td>
                <td>12345</td>
              </tr>
              <tr>
                <th>
                  名称<span className={styles.required}>必須</span>
                </th>
                <td>
                  健康診断実施機関の名称を入力してください
                  <br />
                  ※フリーテキスト120字以内
                </td>
                <td>ジョブカン健康診断病院</td>
              </tr>
              <tr>
                <th>
                  所在地<span className={styles.required}>必須</span>
                </th>
                <td>
                  健康診断実施機関の所在地を入力してください
                  <br />
                  ※フリーテキスト120字以内
                </td>
                <td>東京都渋谷区代々木２－２－１</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
